import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import baseURL from "../component/baseURL";
import showNotification from '../component/Notification';
import stringsoflanguages from '../component/stringsoflanguages';
import FeedSidebar from './FeedSidebar';

export default function CreateNewAvailableFilter() {
    const navigate = useNavigate();
    const location = useLocation();
    const comingFrom = location.state?.comingFrom ?? 'Create';
    const editUserPreferenceId = location.state?.user_preference_id ?? '';
    const [userID, setUserID] = useState('');
    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [userCountryId, setUserCountryId] = useState('');
    const [userStateIds, setUserStateIds] = useState([]);
    const [userCityIds, setUserCityIds] = useState([]);
    const [prefillStateNames, setPrefillStateNames] = useState([]);
    const [prefillCityNames, setPrefillCityNames] = useState([]);
    const [userCountry, setUserCountry] = useState('');
    const [filterName, setFilterName] = useState('');
    const [selectedBedroom, setSelectedBedroom] = useState('');
    const [selectedBathroom, setSelectedBathroom] = useState('');
    const [minValue, setMinValue] = useState('');
    const [maxValue, setMaxValue] = useState('');
    const [propertyType, setPropertyType] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
    const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
    const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
    const stateDropdownRef = useRef(null);
    const cityDropdownRef = useRef(null);
    const countryDropdownRef = useRef(null);

    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        const itemData = location.state?.itemData ?? null;
        setUserID(userid);
        if (itemData) {
            const countryId = itemData.country_id ?? itemData.country_code ?? itemData.country ?? '';
            const countryName = itemData.country_name ?? itemData.country ?? '';
            const stateNamesRaw = itemData.states ?? itemData.state_name ?? itemData.region_name ?? itemData.state ?? itemData.region ?? '';
            const cityNamesRaw = itemData.cities ?? itemData.city_name ?? itemData.city_label ?? '';
            const nextFilterName = itemData.filter_name ?? itemData.filter_title ?? '';
            const nextBedroom = itemData.bedroom ?? itemData.bedrooms ?? itemData.BedroomsTotal ?? '';
            const nextBathroom = itemData.bathroom ?? itemData.bathrooms ?? itemData.BathroomsTotalInteger ?? '';
            const nextMinValue = itemData.min_price ?? itemData.minPrice ?? '';
            const nextMaxValue = itemData.max_price ?? itemData.maxPrice ?? '';
            const nextPropertyType = itemData.property_type ?? itemData.PropertyType ?? '';
            const initialStateNames = String(stateNamesRaw)
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean);
            const initialCityNames = String(cityNamesRaw)
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean);

            setUserCountryId(countryId);
            setUserCountry(countryName);
            setPrefillStateNames(initialStateNames);
            setPrefillCityNames(initialCityNames);
            setFilterName(String(nextFilterName ?? ''));
            setSelectedBedroom(String(nextBedroom ?? ''));
            setSelectedBathroom(String(nextBathroom ?? ''));
            setMinValue(String(nextMinValue ?? ''));
            setMaxValue(String(nextMaxValue ?? ''));
            setPropertyType(String(nextPropertyType ?? ''));
        }
        fetchCountryAPI();
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (stateDropdownRef.current && !stateDropdownRef.current.contains(e.target)) {
                setStateDropdownOpen(false);
            }
            if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
                setCityDropdownOpen(false);
            }
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target)) {
                setCountryDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const selectedStateLabel = useMemo(() => {
        if (!userStateIds.length) return '';
        return stateList
            .filter((s) => userStateIds.includes(String(s.region_code ?? s.state_id ?? s.id ?? '')))
            .map((s) => s.region_name ?? s.state_name ?? s.name ?? '')
            .filter(Boolean)
            .join(', ');
    }, [stateList, userStateIds]);

    const selectedCityLabel = useMemo(() => {
        if (!userCityIds.length) return '';
        return cityList
            .filter((c) => userCityIds.includes(String(c.city_id ?? c.id ?? '')))
            .map((c) => c.city_name ?? c.name ?? '')
            .filter(Boolean)
            .join(', ');
    }, [cityList, userCityIds]);

    const toggleStateSelection = (id) => {
        const nextId = String(id);
        setUserStateIds((prev) => {
            const nextSelectedStateIds = prev.includes(nextId)
                ? prev.filter((v) => v !== nextId)
                : [...prev, nextId];

            setUserCityIds([]);
            setPrefillCityNames([]);
            if (userCountryId && nextSelectedStateIds.length) {
                fetchCityAPI(nextSelectedStateIds, userCountryId);
            } else {
                setCityList([]);
            }
            return nextSelectedStateIds;
        });
    };

    const toggleCitySelection = (id) => {
        const nextId = String(id);
        setUserCityIds((prev) => (
            prev.includes(nextId) ? prev.filter((v) => v !== nextId) : [...prev, nextId]
        ));
    };

    const handleCancel = () => navigate('/feed/new-available-filter');
    const handleSave = () => {
        // API integration can be added in next step.
        navigate('/feed/new-available-filter');
    };

    const handleFeedSidebarClick = (item) => {
        if (item === 'New Available') {
            navigate('/feed/new-available');
        } else if (item === 'Feeds Received') {
            navigate('/feed/feed-recived');
        } else if (item === 'Feeds Requested') {
            navigate('/feed/feeds-requested');
        } else if (item === 'My Feed Shared') {
            navigate('/feed/my-feed-shared');
        } else if (item === 'My Feed Proposed') {
            navigate('/feed/my-feed-proposed');
        } else if (item === 'My Feed Active') {
            navigate('/feed/my-feed-active');
        }
    };


    const normalizeList = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (payload && typeof payload === 'object') return Object.values(payload);
        return [];
    };

    function fetchCountryAPI() {
        fetch(baseURL.URL + '/address/country', { method: 'POST' })
            .then((response) => response.json())
            .then((responseJson) => {
                setCountryList(responseJson.response?.countries || []);
            })
            .catch((error) => {
                console.error('Error fetching countries:', error);
                setCountryList([]);
            });
    }

    function fetchStateAPI(countryCode) {
        if (!countryCode) {
            setStateList([]);
            return;
        }
        const formdata = new FormData();
        formdata.append("country_code", countryCode);
        fetch(baseURL.URL + '/address/state', { method: 'POST', body: formdata })
            .then((response) => response.json())
            .then((responseJson) => {
                const statesPayload = responseJson.response?.states ?? responseJson.response?.regions ?? responseJson.response;
                setStateList(normalizeList(statesPayload));
            })
            .catch((error) => {
                console.error('Error fetching states:', error);
                setStateList([]);
            });
    }

    function fetchCityAPI(stateCodes, countryCode) {
        if (!stateCodes?.length || !countryCode) {
            setCityList([]);
            return;
        }
        const formdata = new FormData();
        formdata.append("country_code", countryCode);
        formdata.append("state_id", stateCodes.join(','));
        fetch(baseURL.URL + '/address/city', { method: 'POST', body: formdata })
            .then((response) => response.json())
            .then((responseJson) => {
                const citiesPayload = responseJson.response?.cities ?? responseJson.response;
                const nextCityList = normalizeList(citiesPayload);
                setCityList(nextCityList);
                if (prefillCityNames.length) {
                    const matchedCityIds = nextCityList
                        .filter((c) => prefillCityNames.includes((c.city_name ?? c.name ?? '').trim()))
                        .map((c) => String(c.city_id ?? c.id ?? ''))
                        .filter(Boolean);
                    if (matchedCityIds.length) setUserCityIds(matchedCityIds);
                    setPrefillCityNames([]);
                }
            })
            .catch((error) => {
                console.error('Error fetching cities:', error);
                setCityList([]);
            });
    }

    useEffect(() => {
        if (userCountryId) {
            fetchStateAPI(userCountryId);
        } else {
            setStateList([]);
        }
    }, [userCountryId]);

    useEffect(() => {
        if (!stateList.length || !prefillStateNames.length) return;
        const matchedStateIds = stateList
            .filter((s) => prefillStateNames.includes((s.region_name ?? s.state_name ?? s.name ?? '').trim()))
            .map((s) => String(s.region_code ?? s.state_id ?? s.id ?? ''))
            .filter(Boolean);
        if (matchedStateIds.length) {
            setUserStateIds(matchedStateIds);
            if (userCountryId) {
                fetchCityAPI(matchedStateIds, userCountryId);
            }
        } else {
            setCityList([]);
            setUserCityIds([]);
        }
        setPrefillStateNames([]);
    }, [stateList, prefillStateNames, userCountryId]);


    function createFilterApi() {
        if (userCountryId !== '') {
            console.log("Selected states: " + userStateIds.join(','));
            console.log("Selected cities: " + userCityIds.join(','));
            let formdata = new FormData();
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
            formdata.append("user_id", userID);     //c81e728d9d4c2f636f067f89cc14862c
            formdata.append("country", userCountryId);
            formdata.append("region", userStateIds.join(','));
            formdata.append("city", userCityIds.join(','));
            formdata.append("filter_type", 'pool');
            formdata.append("filter_name", filterName);
            formdata.append("bedroom", selectedBedroom);
            formdata.append("bathroom", selectedBathroom);
            formdata.append("min_price", minValue);
            formdata.append("max_price", maxValue);
            formdata.append("property_type", propertyType);
            fetch(baseURL.URL + '/portfolio/new_listing_filter_add', { method: 'POST', body: formdata })
                .then((response) => response.json())
                .then((responseJson) => {
                    console.log(responseJson);
                    navigate('/feed/new-available-filter');
                    showNotification('Success', 'Filter created successfully', 'success');
                })
                .catch((error) => {
                    console.error(error);
                    showNotification('Error', 'Failed to create filter', 'danger');
                });
        } else {
            navigate('/feed/new-available-filter');
            showNotification('Error', 'Failed to create filter', 'danger');
        }
    }


    function updateFilterApi() {
        if (userCountryId !== '') {
            let formdata = new FormData();
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
            formdata.append("user_id", userID);     //c81e728d9d4c2f636f067f89cc14862c
            formdata.append("filter_id", editUserPreferenceId);
            formdata.append("country", userCountryId);
            formdata.append("region", userStateIds.join(','));
            formdata.append("city", userCityIds.join(','));
            formdata.append("filter_name", filterName);
            formdata.append("bedroom", selectedBedroom);
            formdata.append("bathroom", selectedBathroom);
            formdata.append("min_price", minValue);
            formdata.append("max_price", maxValue);
            formdata.append("property_type", propertyType);
            fetch(baseURL.URL + '/portfolio/filter_update', { method: 'POST', body: formdata })
                .then((response) => response.json())
                .then((responseJson) => {
                    console.log(responseJson);
                    navigate('/feed/new-available-filter');
                    showNotification('Success', 'Filter updated successfully', 'success');
                })
                .catch((error) => {
                    console.error(error);
                    showNotification('Error', 'Failed to update filter', 'danger');
                });
        } else {
            navigate('/feed/new-available-filter');
            showNotification('Error', 'Failed to update filter', 'danger');
        }
    }

    return (
        <section className="home">
            <div className={`container ${isMobile ? 'feed-page-container-mobile' : ''}`}>
                {isMobile && (
                    <div className="mobile-page-header incoming-member-feed-header flex align-items-center create-new-available-filter-mobile-header">
                        <button type="button" className="mobile-back-btn" onClick={() => navigate('/feed/new-available-filter')} aria-label="Back">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M15 18L9 12L15 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <h2 className="mobile-page-title incoming-member-feed-title">
                            {comingFrom === 'Edit' ? stringsoflanguages.editPortfolioFilter : stringsoflanguages.createPortfolioFilter}
                        </h2>
                    </div>
                )}
                <div className="content-holder">
                    {!isMobile && (
                        <FeedSidebar
                            activeItem="New Available"
                            setActiveItem={handleFeedSidebarClick}
                        />
                    )}
                    <div className="content-right member-create-filter-page member-create-filter-page-new-available">
                        <div className="member-create-filter-card portfolio-create-filter-card">
                            <h3 className="member-create-filter-title">
                                {comingFrom === 'Edit' ? stringsoflanguages.editPortfolioFilter : stringsoflanguages.createPortfolioFilter}
                            </h3>
                            {comingFrom === 'Edit' && (
                                <p className="member-create-filter-subtitle">Filter ID: {editUserPreferenceId}</p>
                            )}

                            <div className="member-create-filter-field">
                                <label>{stringsoflanguages.filterName}</label>
                                <input
                                    type="text"
                                    className="member-multi-dropdown-trigger"
                                    value={filterName}
                                    onChange={(e) => setFilterName(e.target.value)}
                                    placeholder="Enter filter name"
                                />
                            </div>

                            <div className="member-create-filter-field">
                                <label>{stringsoflanguages.country}</label>
                                <div className="member-multi-dropdown" ref={countryDropdownRef}>
                                    <button
                                        type="button"
                                        className="member-multi-dropdown-trigger"
                                        onClick={() => setCountryDropdownOpen((prev) => !prev)}
                                        aria-label="Select country"
                                    >
                                        {userCountry || 'Select Country'}
                                        <i className="fa-solid fa-chevron-down"></i>
                                    </button>
                                    {countryDropdownOpen && (
                                        <div className="member-multi-dropdown-menu">
                                            {countryList.map((c) => {
                                                const optionId = String(c.code ?? c.id ?? '');
                                                const label = c.name ?? c.country_name ?? '';
                                                const selected = optionId && String(userCountryId ?? '') === optionId;
                                                return (
                                                    <button
                                                        key={optionId || label}
                                                        type="button"
                                                        className={`member-single-dropdown-option${selected ? ' is-selected' : ''}`}
                                                        onClick={() => {
                                                            setUserCountryId(optionId);
                                                            setUserCountry(label);
                                                            setCountryDropdownOpen(false);
                                                            setUserStateIds([]);
                                                            setUserCityIds([]);
                                                            setPrefillStateNames([]);
                                                            setPrefillCityNames([]);
                                                            setStateList([]);
                                                            setCityList([]);
                                                            if (optionId) fetchStateAPI(optionId);
                                                        }}
                                                    >
                                                        {label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="member-create-filter-field">
                                <label>{stringsoflanguages.state}</label>
                                <div className="member-multi-dropdown" ref={stateDropdownRef}>
                                    <button
                                        type="button"
                                        className="member-multi-dropdown-trigger"
                                        disabled={!userCountryId}
                                        onClick={() => setStateDropdownOpen((prev) => !prev)}
                                    >
                                        {selectedStateLabel || 'Select State(s)'}
                                        <i className="fa-solid fa-chevron-down"></i>
                                    </button>
                                    {stateDropdownOpen && (
                                        <div className="member-multi-dropdown-menu">
                                            {stateList.map((s) => {
                                                const optionId = String(s.region_code ?? s.state_id ?? s.id ?? '');
                                                const label = s.region_name ?? s.state_name ?? s.name ?? '';
                                                const checked = userStateIds.includes(optionId);
                                                return (
                                                    <label key={optionId} className="member-multi-dropdown-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => toggleStateSelection(optionId)}
                                                        />
                                                        <span>{label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <small className="member-create-filter-help">
                                    {selectedStateLabel ? `Selected: ${selectedStateLabel}` : 'You can select multiple states.'}
                                </small>
                            </div>

                            <div className="member-create-filter-field">
                                <label>{stringsoflanguages.city}</label>
                                <div className="member-multi-dropdown" ref={cityDropdownRef}>
                                    <button
                                        type="button"
                                        className="member-multi-dropdown-trigger"
                                        disabled={!userStateIds.length}
                                        onClick={() => setCityDropdownOpen((prev) => !prev)}
                                    >
                                        {selectedCityLabel || 'Select City(s)'}
                                        <i className="fa-solid fa-chevron-down"></i>
                                    </button>
                                    {cityDropdownOpen && (
                                        <div className="member-multi-dropdown-menu">
                                            {cityList.map((c) => {
                                                const optionId = String(c.city_id ?? c.id ?? '');
                                                const label = c.city_name ?? c.name ?? '';
                                                const checked = userCityIds.includes(optionId);
                                                return (
                                                    <label key={optionId} className="member-multi-dropdown-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => toggleCitySelection(optionId)}
                                                        />
                                                        <span>{label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <small className="member-create-filter-help">
                                    {selectedCityLabel ? `Selected: ${selectedCityLabel}` : 'You can select multiple cities.'}
                                </small>
                            </div>


                            <div className='details-content-row'>
                                <div className="member-create-filter-field">
                                    <label>{stringsoflanguages.bedroom}</label>
                                    <select
                                        className="member-multi-dropdown-trigger"
                                        value={selectedBedroom}
                                        onChange={(e) => setSelectedBedroom(e.target.value)}
                                    >
                                        <option value="ALL">All</option>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                    </select>
                                </div>

                                <div className="member-create-filter-field">
                                    <label>{stringsoflanguages.bathroom}</label>
                                    <select
                                        className="member-multi-dropdown-trigger"
                                        value={selectedBathroom}
                                        onChange={(e) => setSelectedBathroom(e.target.value)}
                                    >
                                        <option value="ALL">All</option>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                    </select>
                                </div>

                                <div className="member-create-filter-field">
                                    <label>{stringsoflanguages.minPrice}</label>
                                    <input
                                        type="number"
                                        className="member-multi-dropdown-trigger"
                                        value={minValue}
                                        onChange={(e) => setMinValue(e.target.value)}
                                        placeholder="Minimum price"
                                    />
                                </div>

                                <div className="member-create-filter-field">
                                    <label>{stringsoflanguages.maxPrice}</label>
                                    <input
                                        type="number"
                                        className="member-multi-dropdown-trigger"
                                        value={maxValue}
                                        onChange={(e) => setMaxValue(e.target.value)}
                                        placeholder="Maximum price"
                                    />
                                </div>
                            </div>

                            <div className="member-create-filter-field">
                                <label>{stringsoflanguages.propertyType}</label>
                                <select
                                    className="member-multi-dropdown-trigger"
                                    value={propertyType}
                                    onChange={(e) => setPropertyType(e.target.value)}
                                >
                                    <option value="ALL">All</option>
                                    <option value="SINGLE FAMILY HOME">Single Family Home</option>
                                    <option value="CONDO / APARTMENT">Condo / Apartment</option>
                                    <option value="MULTI FAMILY HOME">Multi Family Home</option>
                                    <option value="LOT / LAND">Lot / Land</option>
                                    <option value="MOBILE / MANUFACTORED">Mobile / Manufactored</option>
                                    <option value="COMMERCIAL">Commercial</option>
                                </select>
                            </div>

                            <div className="member-create-filter-actions">
                                <button type="button" className="member-create-filter-cancel" onClick={handleCancel}>{stringsoflanguages.cancel}</button>
                                <button type="button" className="member-create-filter-save" onClick={() => comingFrom === 'Create' ? createFilterApi() : updateFilterApi()}>{stringsoflanguages.save}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
